import { DownOutlined } from '@ant-design/icons';
import { IssueCaseDoc, IssueTaskDoc, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { Button, Dropdown, Tooltip, notification } from 'antd';
import { FC } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import { validateCanceledStatusForIssueTask, validateTargetDateForCompletion } from 'src/utils/issue-task-util';
import {
  issueTaskStatusBadgeMap,
  updateIssueTaskWithCaseSync,
  validateIssueTaskStatusForChange,
} from 'src/utils/issue/issue-util';

import { IssueStatusBadge } from '../IssueStatusBadge';

interface IssueTaskStatusSelectorProps {
  issueTaskDoc: IssueTaskDoc;
  issueCaseDoc: IssueCaseDoc;
  disabled: boolean;
}

/**
 * 상태 전환 규칙
 *  PENDING → OPEN, IN_PROGRESS, FAILED, CANCELED
 *  OPEN → IN_PROGRESS, FAILED, CANCELED
 *  IN_PROGRESS → COMPLETED (targetDate 필요), FAILED, CANCELED
 *  COMPLETED → CANCELED (finishedAt 초기화됨)
 *  FAILED → (제한적) *만약 추후 완료로 변경하는 기능을 구현할때는 targetDate가 있는지 추가 검증해야 함
 *  CANCELED → (제한적)
 */
const IssueTaskStatusSelector: FC<IssueTaskStatusSelectorProps> = ({ issueTaskDoc, issueCaseDoc, disabled }) => {
  const items = Object.entries(issueTaskStatusBadgeMap)
    .filter(([key]) => key !== IssueTaskStatus.DELETED.toString())
    .map(([key, value]) => {
      const statusKey = Number(key);
      const validation = validateIssueTaskStatusForChange(issueTaskDoc.status, statusKey);

      let isValid = validation.isValid;
      let errorMessage = validation.errorMessage;

      // COMPLETED로 변경하려는 경우 targetDate가 있는지 추가 검증
      if (isValid) {
        const targetDateValidation = validateTargetDateForCompletion(statusKey, issueTaskDoc.targetDate);
        if (!targetDateValidation.isValid) {
          isValid = false;
          errorMessage = targetDateValidation.errorMessage;
        }
      }

      const badge = (
        <IssueStatusBadge text={value.text} status={value.status} style={!isValid ? { opacity: 0.3 } : {}} />
      );

      return {
        key,
        label:
          !isValid && errorMessage ? (
            <Tooltip title={errorMessage} placement='left'>
              <span style={{ display: 'block' }}>{badge}</span>
            </Tooltip>
          ) : (
            badge
          ),
        disabled: !isValid,
      };
    });

  const onUpdateIssueTaskStatus = async (status0: IssueTaskStatus) => {
    const status = Number(status0);
    const validation = validateIssueTaskStatusForChange(issueTaskDoc.status, status);

    if (!validation.isValid) {
      notification.error({
        message: '태스크 상태 변경에 실패했습니다.',
        description: validation.errorMessage,
      });
      return;
    }

    // COMPLETED로 변경하려는 경우 targetDate가 있는지 추가 검증
    const targetDateValidation = validateTargetDateForCompletion(status, issueTaskDoc.targetDate);
    if (!targetDateValidation.isValid) {
      notification.error({
        message: '태스크 상태 변경에 실패했습니다.',
        description: targetDateValidation.errorMessage,
      });
      return;
    }

    // CANCELED로 변경하려는 경우 추가 검증
    if (status === IssueTaskStatus.CANCELED) {
      const canceledValidation = await validateCanceledStatusForIssueTask(issueTaskDoc);
      if (!canceledValidation.isValid) {
        notification.error({
          message: '태스크 상태 변경에 실패했습니다.',
          description: canceledValidation.errorMessage,
        });
        return;
      }
    }

    try {
      const updateData: Partial<IssueTaskDoc> = { status };

      // COMPLETED 상태로 변경될 때, finishedAt을 자동으로 설정
      if (status === IssueTaskStatus.COMPLETED) {
        updateData.finishedAt = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
      }
      // COMPLETED에서 CANCELED로 변경될 때, finishedAt을 null로 초기화
      else if (issueTaskDoc.status === IssueTaskStatus.COMPLETED && status === IssueTaskStatus.CANCELED) {
        updateData.finishedAt = null;
      }

      await updateIssueTaskWithCaseSync(issueTaskDoc, updateData, issueTaskDoc.issueCaseId, issueCaseDoc.status);
    } catch (error) {
      console.error(error);
      notification.error({
        message: '태스크 상태 변경에 실패했습니다.',
        description: errorObjectToString(error),
      });
    }
  };

  return (
    <Dropdown
      menu={{
        items,
        selectable: true,
        selectedKeys: [issueTaskDoc.status.toString()],
        onSelect: (event) => onUpdateIssueTaskStatus(event.key as unknown as IssueTaskStatus),
      }}
      trigger={['click']}
      disabled={disabled}
    >
      <Button type='text' size='small' icon={<DownOutlined />} iconPosition='end'>
        <IssueStatusBadge
          text={issueTaskStatusBadgeMap[issueTaskDoc.status].text}
          status={issueTaskStatusBadgeMap[issueTaskDoc.status].status}
        />
      </Button>
    </Dropdown>
  );
};

export default IssueTaskStatusSelector;
